import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';

function erf(x) {
  const sign = x < 0 ? -1 : 1;
  x = Math.abs(x);
  const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741, a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;
  const t = 1 / (1 + p * x);
  const y = 1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
  return sign * y;
}
const normCdf = z => 0.5 * (1 + erf(z / Math.sqrt(2)));
const normPdf = z => Math.exp(-0.5 * z * z) / Math.sqrt(2 * Math.PI);

/* Inverse normal CDF via Beasley-Springer-Moro */
function normInv(p) {
  if (p <= 0) return -Infinity; if (p >= 1) return Infinity;
  const a = [-3.969683028665376e+01, 2.209460984245205e+02, -2.759285104469687e+02, 1.383577518672690e+02, -3.066479806614716e+01, 2.506628277459239e+00];
  const b = [-5.447609879822406e+01, 1.615858368580409e+02, -1.556989798598866e+02, 6.680131188771972e+01, -1.328068155288572e+01];
  const c = [-7.784894002430293e-03, -3.223964580411365e-01, -2.400758277161838e+00, -2.549732539343734e+00, 4.374664141464968e+00, 2.938163982698783e+00];
  const d = [7.784695709041462e-03, 3.224671290700398e-01, 2.445134137142996e+00, 3.754408661907416e+00];
  const plow = 0.02425, phigh = 1 - plow;
  let q, r;
  if (p < plow) {
    q = Math.sqrt(-2 * Math.log(p));
    return (((((c[0]*q+c[1])*q+c[2])*q+c[3])*q+c[4])*q+c[5]) / ((((d[0]*q+d[1])*q+d[2])*q+d[3])*q+1);
  }
  if (p <= phigh) {
    q = p - 0.5; r = q * q;
    return (((((a[0]*r+a[1])*r+a[2])*r+a[3])*r+a[4])*r+a[5])*q / (((((b[0]*r+b[1])*r+b[2])*r+b[3])*r+b[4])*r+1);
  }
  q = Math.sqrt(-2 * Math.log(1 - p));
  return -(((((c[0]*q+c[1])*q+c[2])*q+c[3])*q+c[4])*q+c[5]) / ((((d[0]*q+d[1])*q+d[2])*q+d[3])*q+1);
}

/* Miniature normal curve with shaded region: type = 'lt'|'gt'|'between'|'outside'|'zeroToZ' */
function NormalCurve({ type, z1, z2, width = 240, height = 100 }) {
  const xMin = -4, xMax = 4;
  const pad = 6;
  const tx = x => pad + (x - xMin) / (xMax - xMin) * (width - 2 * pad);
  const pmax = normPdf(0);
  const ty = y => height - pad - (y / pmax) * (height - 2 * pad);
  const pts = [];
  for (let i = 0; i <= 100; i++) { const x = xMin + (i / 100) * (xMax - xMin); pts.push([tx(x), ty(normPdf(x))]); }
  const shade = (lo, hi) => {
    const seg = [];
    for (let i = 0; i <= 100; i++) {
      const x = xMin + (i / 100) * (xMax - xMin);
      if (x >= lo && x <= hi) seg.push([tx(x), ty(normPdf(x))]);
    }
    if (seg.length < 2) return null;
    const d = `M${seg[0][0]},${height - pad} ` + seg.map(p => `L${p[0]},${p[1]}`).join(' ') + ` L${seg[seg.length - 1][0]},${height - pad} Z`;
    return <path d={d} fill="#ef4444" opacity="0.55" />;
  };
  let region;
  if (type === 'lt') region = shade(xMin, Math.min(xMax, z1));
  else if (type === 'gt') region = shade(Math.max(xMin, z1), xMax);
  else if (type === 'between') region = shade(Math.max(xMin, Math.min(z1, z2)), Math.min(xMax, Math.max(z1, z2)));
  else if (type === 'outside') region = <>{shade(xMin, Math.min(xMax, Math.min(z1, z2)))}{shade(Math.max(xMin, Math.max(z1, z2)), xMax)}</>;
  else if (type === 'zeroToZ') region = shade(Math.min(0, z1), Math.max(0, z1));
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full max-w-[240px]">
      <line x1={pad} y1={height - pad} x2={width - pad} y2={height - pad} stroke="#94a3b8" />
      <line x1={tx(0)} y1={pad} x2={tx(0)} y2={height - pad} stroke="#cbd5e1" strokeDasharray="2 2" />
      {region}
      <polyline points={pts.map(p => p.join(',')).join(' ')} fill="none" stroke="#1e40af" strokeWidth="1.6" />
    </svg>
  );
}

/* Main Z-score calc from x, mean, sd */
function MainZScore() {
  const [x, setX] = useState(5);
  const [mean, setMean] = useState(3);
  const [sd, setSd] = useState(2);
  const c = useMemo(() => {
    if (sd <= 0) return null;
    const z = (x - mean) / sd;
    return {
      z,
      pLt: normCdf(z),
      pGt: 1 - normCdf(z),
      pBetween: Math.abs(normCdf(z) - 0.5),
      pOutside: 2 * (1 - normCdf(Math.abs(z))),
      pMidToZ: Math.abs(normCdf(z) - 0.5),
      pTwoTail: 2 * (1 - normCdf(Math.abs(z))),
      pPct: normCdf(z) * 100
    };
  }, [x, mean, sd]);
  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900">Z-score Calculator</h3>
      <p className="text-sm text-slate-600 mt-1">Use this calculator to compute the z-score of a raw value under a normal distribution.</p>
      <div className="mt-3 bg-slate-100 rounded-xl p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="text-sm">Raw Score (x)<input type="number" step="any" value={x} onChange={e => setX(+e.target.value || 0)} className="mt-1 w-full px-3 py-2 rounded border border-slate-300 bg-white" /></label>
        <label className="text-sm">Population Mean (μ)<input type="number" step="any" value={mean} onChange={e => setMean(+e.target.value || 0)} className="mt-1 w-full px-3 py-2 rounded border border-slate-300 bg-white" /></label>
        <label className="text-sm">Std Deviation (σ)<input type="number" step="any" value={sd} onChange={e => setSd(+e.target.value || 0)} className="mt-1 w-full px-3 py-2 rounded border border-slate-300 bg-white" /></label>
      </div>
      {c && (
        <div className="mt-4 bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 bg-[#4c7b25] text-white -mx-4 -mt-4 mb-3 px-4 py-2 rounded-t-xl"><span className="text-sm font-semibold">Result</span></div>
          <div className="text-2xl font-bold text-blue-700 mb-3">Z-score = {c.z.toFixed(6)}</div>
          <table className="w-full text-sm border border-slate-200">
            <tbody>
              <tr><td className="px-3 py-2">Probability of x&lt;{x}: <b>{c.pLt.toFixed(5)}</b></td><td className="px-3 py-2 w-64"><NormalCurve type="lt" z1={c.z} /></td></tr>
              <tr className="bg-slate-50"><td className="px-3 py-2">Probability of x&gt;{x}: <b>{c.pGt.toFixed(5)}</b></td><td className="px-3 py-2"><NormalCurve type="gt" z1={c.z} /></td></tr>
              <tr><td className="px-3 py-2">Probability of {Math.min(mean, x)}&lt;x&lt;{Math.max(mean, x)}: <b>{c.pMidToZ.toFixed(5)}</b></td><td className="px-3 py-2"><NormalCurve type="zeroToZ" z1={c.z} /></td></tr>
            </tbody>
          </table>
          <StepsPanel steps={[
            `Z = (x − μ) / σ = (${x} − ${mean}) / ${sd} = <b>${c.z.toFixed(4)}</b>`,
            `P(x&lt;${x}) = Φ(Z) = <b>${c.pLt.toFixed(5)}</b>`,
            `P(x&gt;${x}) = 1 − Φ(Z) = <b>${c.pGt.toFixed(5)}</b>`
          ]} />
        </div>
      )}
    </div>
  );
}

/* Sub: Probability between Two Z-scores */
function ProbBetweenTwoZ() {
  const [z1, setZ1] = useState(-1);
  const [z2, setZ2] = useState(0);
  const c = useMemo(() => {
    const lo = Math.min(z1, z2), hi = Math.max(z1, z2);
    const pBet = normCdf(hi) - normCdf(lo);
    return {
      lo, hi,
      pBetween: pBet,
      pOutside: 1 - pBet,
      pLtLo: normCdf(lo),
      pGtHi: 1 - normCdf(hi)
    };
  }, [z1, z2]);
  return (
    <div className="mt-6">
      <h3 className="text-lg font-bold text-slate-900">Probability between Two Z-scores</h3>
      <p className="text-sm text-slate-600 mt-1">Use this calculator to find the probability (area P in the diagram) between two z-scores.</p>
      <div className="mt-3 bg-slate-100 rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="text-sm">Left Bound, Z₁<input type="number" step="any" value={z1} onChange={e => setZ1(+e.target.value || 0)} className="mt-1 w-full px-3 py-2 rounded border border-slate-300 bg-white" /></label>
        <label className="text-sm">Right Bound, Z₂<input type="number" step="any" value={z2} onChange={e => setZ2(+e.target.value || 0)} className="mt-1 w-full px-3 py-2 rounded border border-slate-300 bg-white" /></label>
      </div>
      <div className="mt-4 bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-2 bg-[#4c7b25] text-white -mx-4 -mt-4 mb-3 px-4 py-2 rounded-t-xl"><span className="text-sm font-semibold">Result</span></div>
        <table className="w-full text-sm border border-slate-200">
          <tbody>
            <tr><td className="px-3 py-2">P({c.lo.toFixed(2)}&lt;x&lt;{c.hi.toFixed(2)}) = <b>{c.pBetween.toFixed(5)}</b></td><td className="w-64 px-3 py-2"><NormalCurve type="between" z1={c.lo} z2={c.hi} /></td></tr>
            <tr className="bg-slate-50"><td className="px-3 py-2">P(x&lt;{c.lo.toFixed(2)} or x&gt;{c.hi.toFixed(2)}) = <b>{c.pOutside.toFixed(5)}</b></td><td className="px-3 py-2"><NormalCurve type="outside" z1={c.lo} z2={c.hi} /></td></tr>
            <tr><td className="px-3 py-2">P(x&lt;{c.lo.toFixed(2)}) = <b>{c.pLtLo.toFixed(5)}</b></td><td className="px-3 py-2"><NormalCurve type="lt" z1={c.lo} /></td></tr>
            <tr className="bg-slate-50"><td className="px-3 py-2">P(x&gt;{c.hi.toFixed(2)}) = <b>{c.pGtHi.toFixed(5)}</b></td><td className="px-3 py-2"><NormalCurve type="gt" z1={c.hi} /></td></tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* Sub: Z-score and Probability Converter */
function ZAndProbConverter() {
  const [z, setZ] = useState(2);
  const c = useMemo(() => ({
    pLt: normCdf(z),
    pGt: 1 - normCdf(z),
    pMidToZ: Math.abs(normCdf(z) - 0.5),
    pInside: normCdf(Math.abs(z)) - normCdf(-Math.abs(z)),
    pOutside: 2 * (1 - normCdf(Math.abs(z)))
  }), [z]);
  return (
    <div className="mt-6">
      <h3 className="text-lg font-bold text-slate-900">Z-score and Probability Converter</h3>
      <p className="text-sm text-slate-600 mt-1">Provide the z-score to see all five probability regions of a standard normal distribution.</p>
      <div className="mt-3 bg-slate-100 rounded-xl p-4">
        <label className="text-sm block max-w-xs">Z-score, Z<input type="number" step="any" value={z} onChange={e => setZ(+e.target.value || 0)} className="mt-1 w-full px-3 py-2 rounded border border-slate-300 bg-white" /></label>
      </div>
      <div className="mt-4 bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-2 bg-[#4c7b25] text-white -mx-4 -mt-4 mb-3 px-4 py-2 rounded-t-xl"><span className="text-sm font-semibold">Result</span></div>
        <div className="text-lg font-semibold text-slate-800 mb-3">Given Z = {z}</div>
        <table className="w-full text-sm border border-slate-200">
          <tbody>
            <tr><td className="px-3 py-2">P(x&lt;Z) = <b>{c.pLt.toFixed(5)}</b></td><td className="w-64 px-3 py-2"><NormalCurve type="lt" z1={z} /></td></tr>
            <tr className="bg-slate-50"><td className="px-3 py-2">P(x&gt;Z) = <b>{c.pGt.toFixed(5)}</b></td><td className="px-3 py-2"><NormalCurve type="gt" z1={z} /></td></tr>
            <tr><td className="px-3 py-2">P(0&lt;x&lt;Z) = <b>{c.pMidToZ.toFixed(5)}</b></td><td className="px-3 py-2"><NormalCurve type="zeroToZ" z1={z} /></td></tr>
            <tr className="bg-slate-50"><td className="px-3 py-2">P(-Z&lt;x&lt;Z) = <b>{c.pInside.toFixed(5)}</b></td><td className="px-3 py-2"><NormalCurve type="between" z1={-Math.abs(z)} z2={Math.abs(z)} /></td></tr>
            <tr><td className="px-3 py-2">P(x&lt;-Z or x&gt;Z) = <b>{c.pOutside.toFixed(5)}</b></td><td className="px-3 py-2"><NormalCurve type="outside" z1={-Math.abs(z)} z2={Math.abs(z)} /></td></tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ZScoreCalc() {
  return (
    <CalcShell look="cnet" title="Z-score Calculator" subtitle="Modify the values and click the Calculate button to use">
      <MainZScore />
      <ProbBetweenTwoZ />
      <ZAndProbConverter />
    </CalcShell>
  );
}

export { normCdf, normInv, NormalCurve };
